import React, { useEffect, useState, useCallback } from 'react';
import { formatFastApiDetail } from '../utils/fastApiError';

function StatCard({ label, value, hint }) {
  return (
    <div className="glass cardPad">
      <div className="statNum">{value}</div>
      <div className="statLabel">{label}</div>
      {hint ? <div className="small" style={{ marginTop: 8 }}>{hint}</div> : null}
    </div>
  );
}

function formatTs(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts * 1000);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
}

function CooperationLeads() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async (curPage, curQ) => {
    setLoading(true);
    setErr('');
    try {
      const params = new URLSearchParams({ page: String(curPage), page_size: String(pageSize) });
      if (curQ) params.set('q', curQ);
      const res = await fetch(`/api/admin/cooperation/leads?${params}`, { credentials: 'include' });
      if (!res.ok) {
        setErr(`加载失败（HTTP ${res.status}）`);
        setLoading(false);
        return;
      }
      const json = await res.json();
      setItems(json.items || []);
      setTotal(json.total || 0);
    } catch {
      setErr('网络错误，加载失败');
    } finally {
      setLoading(false);
    }
  }, [pageSize]);

  useEffect(() => { load(page, q); }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const maxPage = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="glass cardPad" style={{ marginTop: 20 }}>
      <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h3 className="h1" style={{ fontSize: 20 }}>合作意向（{total}）</h3>
          <p className="subtle">来自 /cooperation.html 页面的访客提交，最新在前。</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            className="textArea"
            style={{ minHeight: 36, height: 36, width: 220, padding: '8px 10px' }}
            placeholder="搜索姓名/公司/电话/邮箱…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(1); load(1, q); } }}
          />
          <button className="pillBtn" onClick={() => { setPage(1); load(1, q); }} disabled={loading}>
            {loading ? '加载中…' : '搜索'}
          </button>
        </div>
      </div>
      <div className="divider" />
      {err ? <span className="tag tagWarn">{err}</span> : null}
      {items.length === 0 && !loading && !err ? (
        <div className="subtle" style={{ padding: '12px 0' }}>暂无合作意向提交。</div>
      ) : null}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 }}>
          <thead>
            <tr style={{ textAlign: 'left', color: '#9aa' }}>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>时间</th>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>姓名</th>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>公司</th>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>电话</th>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>邮箱</th>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>方向</th>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)', minWidth: 240 }}>需求描述</th>
              <th style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.12)' }}>IP</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} style={{ verticalAlign: 'top' }}>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'nowrap' }}>{formatTs(it.created_at)}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{it.name}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{it.company || '—'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{it.phone || '—'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{it.email || '—'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{it.direction || '—'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', whiteSpace: 'pre-wrap' }}>{it.message || '—'}</td>
                <td style={{ padding: '8px 6px', borderBottom: '1px solid rgba(255,255,255,0.06)', color: '#9aa' }}>{it.ip || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > pageSize ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button className="pillBtn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || loading}>上一页</button>
          <span className="subtle" style={{ alignSelf: 'center' }}>{page} / {maxPage}</span>
          <button className="pillBtn" onClick={() => setPage((p) => Math.min(maxPage, p + 1))} disabled={page >= maxPage || loading}>下一页</button>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  useEffect(() => {
    let mounted = true;
    (async () => {
      setErr('');
      const res = await fetch('/api/admin/stats', { credentials: 'include' });
      if (!mounted) return;
      if (!res.ok) {
        setErr('无法获取统计数据');
        return;
      }
      const json = await res.json();
      setData(json);
    })().catch(() => mounted && setErr('无法获取统计数据'));
    return () => { mounted = false; };
  }, []);

  if (err) {
    return (
      <div className="glass cardPad">
        <span className="tag tagWarn">{err}</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 className="h1">Dashboard</h2>
        <p className="subtle">最近访问与问答概览。</p>
      </div>
      <div className="grid2">
        <StatCard
          label="今日问答次数"
          value={data?.today?.chat_count ?? '—'}
          hint={data?.today?.date ? `Date: ${data.today.date}` : ''}
        />
        <StatCard
          label="今日独立 IP（对话）"
          value={data?.today?.unique_ips ?? '—'}
        />
        <StatCard
          label="今日访客数"
          value={data?.today?.visitor_count ?? '—'}
        />
        <StatCard
          label="今日简历下载"
          value={data?.today?.download_count ?? '—'}
        />
        <StatCard
          label="近 7 天问答次数"
          value={data?.last7d?.chat_count ?? '—'}
        />
        <StatCard
          label="近 7 天独立 IP（对话）"
          value={data?.last7d?.unique_ips ?? '—'}
        />
        <StatCard
          label="近 7 天访客数"
          value={data?.last7d?.visitor_count ?? '—'}
        />
        <StatCard
          label="近 7 天简历下载"
          value={data?.last7d?.download_count ?? '—'}
        />
      </div>

      <div className="glass cardPad" style={{ marginTop: 20 }}>
        <div style={{ marginBottom: 8 }}>
          <h3 className="h1" style={{ fontSize: 20 }}>更换简历</h3>
          <p className="subtle">上传新的 PDF 简历，将同时更新下载文件和向量数据库索引。</p>
        </div>
        <div className="divider" />
        <div className="toolbar">
          <label className="pillBtn" style={{ cursor: uploading ? 'not-allowed' : 'pointer', opacity: uploading ? 0.6 : 1 }}>
            {uploading ? '上传并索引中…' : '选择 PDF 文件'}
            <input
              type="file"
              accept=".pdf"
              disabled={uploading}
              style={{ display: 'none' }}
              onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                if (!file.name.toLowerCase().endsWith('.pdf')) {
                  setUploadMsg('只支持 PDF 文件');
                  return;
                }
                setUploading(true);
                setUploadMsg('');
                try {
                  const fd = new FormData();
                  fd.append('file', file);
                  const res = await fetch('/api/admin/upload-resume', {
                    method: 'POST',
                    body: fd,
                    credentials: 'include',
                  });
                  const raw = await res.text();
                  let json = {};
                  try {
                    json = raw ? JSON.parse(raw) : {};
                  } catch {
                    json = {};
                  }
                  if (res.ok) {
                    setUploadMsg(json.message || '简历已更新');
                  } else {
                    const detail = formatFastApiDetail(json.detail);
                    if (detail) {
                      setUploadMsg(detail);
                    } else if (res.status === 401) {
                      setUploadMsg('未登录或登录已过期，请退出后重新登录再上传');
                    } else if (res.status === 413) {
                      setUploadMsg('文件过大被拒绝（请在 nginx 中增大 client_max_body_size，例如 20m）');
                    } else if (raw && raw.length < 200 && raw.trim().startsWith('<')) {
                      setUploadMsg(`上传失败（HTTP ${res.status}，服务端返回了非 JSON 页面，多为网关或反代限制）`);
                    } else {
                      setUploadMsg(`上传失败（HTTP ${res.status}）${raw ? `：${raw.slice(0, 120)}` : ''}`);
                    }
                  }
                } catch {
                  setUploadMsg('网络错误，上传失败');
                } finally {
                  setUploading(false);
                  e.target.value = '';
                }
              }}
            />
          </label>
        </div>
        {uploadMsg ? (
          <div style={{ marginTop: 12 }}>
            <span className={`tag ${uploadMsg.includes('失败') || uploadMsg.includes('错误') ? 'tagWarn' : 'tagSuccess'}`}>
              {uploadMsg}
            </span>
          </div>
        ) : null}
      </div>

      <CooperationLeads />
    </div>
  );
}


